import { useCallback, useEffect, useState } from 'react';
import { getAdminSession } from '../adminSession';
import {
  fetchPayrollRecords,
  getPayrollSettingsErrorMessage,
  type PayrollRecord,
} from './payrollSettingsApi';
import { numberKRW } from './payrollFormat';
import './PayrollRecords.scss';

type Props = {
  onBack: () => void;
  onSelect: (id: number) => void;
};

export default function PayrollRecordsList({ onBack, onSelect }: Props) {
  const session = getAdminSession();

  const [records, setRecords] = useState<PayrollRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const loadRecords = useCallback(async () => {
    if (!session?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPayrollRecords(session.id, {
        year: yearFilter ? Number(yearFilter) : undefined,
        month: monthFilter ? Number(monthFilter) : undefined,
        employeeName: nameFilter.trim() || undefined,
      });
      setRecords(data.items);
    } catch (err) {
      setError(getPayrollSettingsErrorMessage(err, '급여명세서 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [session?.id, yearFilter, monthFilter, nameFilter]);

  useEffect(() => {
    void loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  return (
    <div className="payroll-records-list">
      <div className="payroll-records-list__header">
        <button type="button" className="payroll-records-list__back-btn" onClick={onBack}>
          ← 급여 계산기로
        </button>
        <h3 className="payroll-records-list__title">지난 급여 목록</h3>
      </div>

      <div className="payroll-records-list__filters">
        <input
          type="text"
          inputMode="numeric"
          placeholder="년도 (예: 2026)"
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value.replace(/[^\d]/g, ''))}
        />
        <input
          type="text"
          inputMode="numeric"
          placeholder="월 (1~12)"
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value.replace(/[^\d]/g, ''))}
        />
        <input
          type="text"
          placeholder="직원 이름 검색"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />
        <button type="button" onClick={() => void loadRecords()} disabled={loading}>
          {loading ? '조회 중…' : '조회'}
        </button>
      </div>

      {error && <p className="payroll-records-list__error">{error}</p>}

      <div className="payroll-records-list__table-wrap">
        <table className="payroll-records-list__table">
          <thead>
            <tr>
              <th>구분</th>
              <th>대상년월</th>
              <th>직원</th>
              <th>소속</th>
              <th>세전 월급</th>
              <th>총 공제</th>
              <th>실수령액</th>
              <th>저장일시</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 && (
              <tr>
                <td colSpan={8} className="payroll-records-list__empty">
                  저장된 급여명세서가 없습니다.
                </td>
              </tr>
            )}
            {records.map((record) => (
              <tr
                key={record.id}
                className="payroll-records-list__row"
                onClick={() => onSelect(record.id)}
              >
                <td>{record.payType === 'salary' ? '정직원' : '시급'}</td>
                <td>{record.year}년 {record.month}월</td>
                <td>{record.employeeName}</td>
                <td>{[record.department, record.position].filter(Boolean).join(' · ') || '-'}</td>
                <td>{numberKRW(record.grossPay)}</td>
                <td>{numberKRW(record.totalDeductions)}</td>
                <td>{numberKRW(record.netPay)}</td>
                <td>{record.updatedAt ? new Date(record.updatedAt).toLocaleString('ko-KR') : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
